import { useState, useEffect, useCallback, useRef } from 'react';
import api, { apiRoutes } from '../api';
import { getConfig } from '../config/environment';
import { getAuthorizedContentAdminToken } from '../utils/adminToken';

/**
 * Hook to fetch and manage custom pages from the API.
 * Fires 'customPagesChanged' event on mutations so other components re-render.
 */
export function useCustomPages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const fetchPages = useCallback(async () => {
    try {
      setLoading(true);
      const { tlfId } = getConfig();
      const adminToken = getAuthorizedContentAdminToken(tlfId);

      let response;
      if (adminToken) {
        response = await api.get(apiRoutes.AdminCustomPages);
      } else {
        response = await api.get(
          `${apiRoutes.PublicCustomPages}?organizationId=${encodeURIComponent(tlfId)}`
        );
      }

      const payload = response?.data?.payload;
      const fetchedPages = (payload?.pages || []).map(normalizePage);
      if (mountedRef.current) {
        setPages(fetchedPages);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching custom pages:', err);
      if (mountedRef.current) {
        setError(err.message);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchPages();
    return () => { mountedRef.current = false; };
  }, [fetchPages]);

  useEffect(() => {
    const handler = () => fetchPages();
    window.addEventListener('customPagesChanged', handler);
    return () => window.removeEventListener('customPagesChanged', handler);
  }, [fetchPages]);

  const savePage = useCallback(async (pageDto) => {
    const response = await api.put(apiRoutes.AdminCustomPages, pageDto);
    const saved = response?.data?.payload?.page;
    window.dispatchEvent(new CustomEvent('customPagesChanged'));
    return saved ? normalizePage(saved) : null;
  }, []);

  const deletePage = useCallback(async (pageId) => {
    await api.delete(`${apiRoutes.AdminCustomPages}/${pageId}`);
    window.dispatchEvent(new CustomEvent('customPagesChanged'));
  }, []);

  return { pages, loading, error, savePage, deletePage, refetch: fetchPages };
}

function normalizePage(raw) {
  let titles = raw.titles || {};
  if (typeof titles === 'string') {
    try { titles = JSON.parse(titles); } catch { titles = {}; }
  }
  return {
    id: raw.id || '',
    slug: raw.slug || '',
    pageName: raw.pageName || raw.page_name || '',
    titles,
    enabled: raw.enabled !== false,
    order: raw.sortOrder ?? raw.order ?? 0,
  };
}

/**
 * Lightweight hook for navigation — fetches visible pages via public endpoint.
 * Caches in memory and localStorage to avoid refetching on every route change and prevent layout shifts on refresh.
 */
let navPagesCache = null;
let navPagesCacheTime = 0;
const NAV_CACHE_TTL = 30000;
const VISIBLE_PAGES_STORAGE_KEY = 'site_visible_custom_pages';

function getInitialNavPages() {
  if (navPagesCache) return navPagesCache;
  try {
    const saved = localStorage.getItem(VISIBLE_PAGES_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        navPagesCache = parsed;
        return parsed;
      }
    }
  } catch {}
  return [];
}

export function useVisibleCustomPages() {
  const [pages, setPages] = useState(getInitialNavPages);
  const [loading, setLoading] = useState(() => !navPagesCache);

  useEffect(() => {
    let cancelled = false;

    const fetchVisible = async () => {
      if (navPagesCache && Date.now() - navPagesCacheTime < NAV_CACHE_TTL) {
        setPages(navPagesCache);
        setLoading(false);
        return;
      }

      try {
        const { tlfId } = getConfig();
        const response = await api.get(
          `${apiRoutes.PublicCustomPages}?organizationId=${encodeURIComponent(tlfId)}`
        );
        const payload = response?.data?.payload;
        const fetched = (payload?.pages || [])
          .filter(p => p.enabled !== false)
          .map(normalizePage);
        navPagesCache = fetched;
        navPagesCacheTime = Date.now();
        try {
          localStorage.setItem(VISIBLE_PAGES_STORAGE_KEY, JSON.stringify(fetched));
        } catch {}
        if (!cancelled) {
          setPages(fetched);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };

    fetchVisible();

    const handler = () => {
      navPagesCache = null;
      try { localStorage.removeItem(VISIBLE_PAGES_STORAGE_KEY); } catch {}
      fetchVisible();
    };
    window.addEventListener('customPagesChanged', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('customPagesChanged', handler);
    };
  }, []);

  return { pages, loading };
}
